import { Link } from "react-router-dom";
import { PageHeader } from "../components/EmptyState";
import { SystemDiagram } from "../components/SystemDiagram";

// In-app connection guide. Plain, honest copy — what talks to what, and the
// exact steps to connect each piece of hardware.

export default function Help() {
  return (
    <div className="mx-auto max-w-2xl animate-fade-in">
      <PageHeader title="Help" eyebrow="How it works & connecting devices" />

      {/* The big picture — system map */}
      <SystemMap />

      {/* How the data flows */}
      <section className="card-pad mt-4">
        <h2 className="section-title mb-3">How your data gets here</h2>
        <p className="text-ink-muted text-[15px] leading-relaxed">
          Three independent connections feed this app. There is no single
          "Google account" sign-in — each device uses its own path:
        </p>
        <div className="mt-4 space-y-3">
          <Flow
            label="Withings scale"
            chain={["Scale", "Withings cloud", "Here"]}
            note="Direct cloud link. One-click web login."
          />
          <Flow
            label="Garmin"
            chain={["Watch", "Garmin cloud", "Here"]}
            note="Direct cloud link. One-click web login (after setup)."
          />
          <Flow
            label="Galaxy Watch"
            chain={["Watch", "Samsung Health", "Health Connect (on phone)", "Companion app", "Here"]}
            note="Health Connect is an on-phone Google datastore — not a cloud API. A small app on your phone reads it and sends it here, so this one needs a few setup steps."
          />
        </div>
      </section>

      {/* Withings */}
      <Guide id="withings" title="Withings scale" badge="One-time setup first" tone="warn">
        <Step n={0}>
          <b>One-time setup (admin).</b> At <b>developer.withings.com</b> create an
          app — type <b>Public API integration</b>, cloud <b>Withings Public
          Cloud</b>. For the <b>registered / callback URL</b>, use exactly:
          <CodeBlock>https://health-api.tallyio.com.au/auth/withings/callback</CodeBlock>
          Withings pings that URL to verify it (it must return 200 and the Worker
          must be deployed — both are handled). Then copy the credentials into the
          Worker from <code className="text-xs">apps/api</code>:
          <CodeBlock>
            wrangler secret put WITHINGS_CLIENT_ID{"\n"}wrangler secret put WITHINGS_CLIENT_SECRET
          </CodeBlock>
          Finally add a Cloudflare Access <b>Bypass</b> for{" "}
          <code className="text-xs break-all">health-api.tallyio.com.au/webhooks/withings</code>{" "}
          so weigh-in notifications reach the Worker (the login pages stay behind
          Access).
        </Step>
        <Step n={1}>
          In the <b>Withings Health Mate</b> app, create an account and pair your
          scale (one-time).
        </Step>
        <Step n={2}>
          Here, go to <NavTo to="/connections">Connections</NavTo> → <b>Withings</b>{" "}
          → <b>Connect</b>, then log in to Withings and approve access.
        </Step>
        <Step n={3}>
          Step on the scale — a weigh-in appears within about two minutes. Your
          past weigh-ins backfill automatically, so the weight trend fills in
          straight away.
        </Step>
      </Guide>

      {/* Garmin */}
      <Guide id="garmin" title="Garmin" badge="One-time setup first" tone="warn">
        <Step n={0}>
          <b>Setup (once):</b> Garmin's Health API requires approval through their
          Developer Program before the connection can be switched on. This is an
          admin step and can take a few days. Until it's done, the Connect button
          will say it's not configured.
        </Step>
        <Step n={1}>
          Once enabled, go to <NavTo to="/connections">Connections</NavTo> →{" "}
          <b>Garmin</b> → <b>Connect</b>, log in to Garmin and approve.
        </Step>
        <Step n={2}>
          Recorded activities, sleep, steps and heart rate flow in from Garmin's
          cloud automatically — no app on your phone needed.
        </Step>
      </Guide>

      {/* Galaxy Watch */}
      <Guide id="samsung" title="Galaxy Watch" badge="Most involved" tone="warn">
        <Step n={1}>
          <b>Build &amp; install the bridge app.</b> It isn't in the Play Store —
          open the project's <code className="text-xs">bridge/</code> folder in{" "}
          <b>Android Studio</b>, then <b>Build → Build APK</b> and sideload it to
          your phone (<code className="text-xs">adb install</code>, or copy the
          .apk over and tap it). This app reads Health Connect and sends data here.
        </Step>
        <Step n={2}>
          In <b>Samsung Health</b>: Settings → <b>Health Connect</b> → grant
          read access for steps, heart rate, sleep and exercise. Also make sure
          Samsung Cloud sync is on.
        </Step>
        <Step n={3}>
          Open the bridge app, grant it the Health Connect permissions, and enter
          the server address and the bridge secret.
        </Step>
        <Step n={4}>
          <b>Important:</b> exclude the bridge app from battery optimisation
          (Settings → Apps → the bridge app → Battery → <b>Unrestricted</b>).
          Without this, Samsung's power saving silently stops syncing after a few
          days.
        </Step>
        <p className="text-ink-subtle text-small mt-3">
          Freshness note: Galaxy data arrives "within an hour", not in real time —
          the watch batches heart rate to save battery, and the phone syncs on a
          schedule. For live numbers, Samsung Health on the watch is the place to
          look; this app is the history and trends.
        </p>
      </Guide>

      {/* Troubleshooting */}
      <section className="card-pad">
        <h2 className="section-title mb-3">If something stops showing up</h2>
        <ul className="space-y-2 text-ink-muted text-[15px]">
          <li>
            <b>Check <NavTo to="/connections">Connections</NavTo></b> — each
            provider shows its last sync time. A stale time points at the problem
            source.
          </li>
          <li>
            <b>Galaxy Watch went quiet?</b> Almost always battery optimisation —
            re-check the bridge app is set to Unrestricted, then open it and tap
            sync.
          </li>
          <li>
            <b>Withings/Garmin?</b> Re-connect from the Connections page; a token
            can expire if access was revoked on their side.
          </li>
        </ul>
      </section>
    </div>
  );
}

// SystemMap — the architecture at a glance, drawn as an SVG (SystemDiagram),
// with a one-line legend underneath.
function SystemMap() {
  return (
    <section className="card-pad">
      <h2 className="section-title mb-1">The big picture</h2>
      <p className="text-ink-subtle text-small mb-3">
        Where every number comes from and how it reaches your screen.
      </p>
      <SystemDiagram />
      <p className="text-ink-subtle text-small mt-3">
        Machine traffic (devices, scale, AI) enters the Worker's{" "}
        <span className="text-accent">side door</span> with secrets/OAuth; you reach the{" "}
        <span className="text-accent">front door</span> behind Cloudflare Access. The dashed
        box is the only piece you install — the phone bridge app.
      </p>
    </section>
  );
}

function Flow({ label, chain, note }: { label: string; chain: string[]; note: string }) {
  return (
    <div className="rounded-md border border-ink-line bg-canvas p-3">
      <div className="text-ink font-medium text-small mb-1.5">{label}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {chain.map((c, i) => (
          <span key={c} className="flex items-center gap-1.5">
            <span className="pill-neutral">{c}</span>
            {i < chain.length - 1 && <span className="text-ink-subtle">→</span>}
          </span>
        ))}
      </div>
      <p className="text-ink-subtle text-small mt-2">{note}</p>
    </div>
  );
}

function Guide({
  id,
  title,
  badge,
  tone,
  children,
}: {
  id: string;
  title: string;
  badge: string;
  tone: "positive" | "warn";
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="card-pad mt-4 scroll-mt-20">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="section-title">{title}</h2>
        <span className={tone === "positive" ? "pill-positive" : "pill-warn"}>{badge}</span>
      </div>
      <ol className="space-y-3">{children}</ol>
    </section>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 grid h-6 w-6 place-items-center rounded-full bg-accent/10 text-accent text-xs font-semibold tabular-nums">
        {n}
      </span>
      <span className="text-ink-muted text-[15px] leading-relaxed">{children}</span>
    </li>
  );
}

function NavTo({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="text-accent hover:underline font-medium">
      {children}
    </Link>
  );
}

function CodeBlock({ children }: { children: React.ReactNode }) {
  return (
    <pre className="mt-1.5 mb-1 whitespace-pre-wrap break-all rounded-md border border-ink-line bg-ink-line/30 px-2.5 py-1.5 font-mono text-xs text-ink">
      {children}
    </pre>
  );
}
